import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const { user, loading: authLoading, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();

  // Check for error in URL hash (e.g., expired token)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorParam = hashParams.get("error");
    const errorCode = hashParams.get("error_code");
    const errorDescription = hashParams.get("error_description");

    if (errorParam || errorCode) {
      let message = "This password reset link is invalid or has expired.";

      if (errorCode === "otp_expired") {
        message =
          "This password reset link has expired. Password reset links are only valid for 1 hour.";
      } else if (errorDescription) {
        message = errorDescription.replace(/\+/g, " ");
      }

      setLinkError(message);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(password);
      await signOut(); // force a fresh login to confirm the new password works
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message || "Failed to update password.");
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="text-center py-12 dark:text-white">Loading...</div>;
  }

  // Show error if there's an error in the URL
  if (linkError) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <div className="bg-white rounded-lg shadow-md p-8 dark:bg-gray-800">
          <h1 className="text-2xl font-bold mb-4 dark:text-white">
            Link Expired
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{linkError}</p>
          <Link
            to="/forgot-password"
            className="text-blue-600 dark:text-blue-400 underline"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  // No session = link expired/invalid, or user navigated here directly
  if (!user) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <div className="bg-white rounded-lg shadow-md p-8 dark:bg-gray-800">
          <h1 className="text-2xl font-bold mb-4 dark:text-white">
            Link Expired
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            This password reset link is invalid or has expired.
          </p>
          <Link
            to="/forgot-password"
            className="text-blue-600 dark:text-blue-400 underline"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-20">
      <div className="bg-white rounded-lg shadow-md p-8 dark:bg-gray-800">
        <h1 className="text-3xl font-bold mb-6 text-center dark:text-white">
          Set New Password
        </h1>

        {done ? (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-center dark:bg-green-950/30 dark:border-green-800 dark:text-green-300">
            Password updated! Redirecting to login...
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <div>
              <label className="block font-semibold mb-2 dark:text-white">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className="block font-semibold mb-2 dark:text-white">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                autoComplete="new-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold
                                hover:bg-orange-700 transition disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
